'use client';

import Link from 'next/link';
import { styled } from 'styled-components';

import BackToBlogLink from '@dvd/blog/components/BackToBlogLink';
import { tagStyles } from '@dvd/components/Tag';
import {
  BLOG_CATEGORY_DATA,
  categoryUrl,
  type BlogCategoryKey,
} from '@lib/data/blogCategories';
import { trackLink } from '@lib/tracking';

type BreadcrumbsProps = {
  /**
   * Blog category tags to display. Callers cap the count (the page passes
   * at most 3); the 1–3 bound itself is enforced by the frontmatter schema.
   */
  tags?: BlogCategoryKey[];
};

const BreadcrumbsBar = styled.nav`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[0.75]};
  padding-block: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.large} {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[0.25]};
    padding-block: ${({ theme }) => theme.spacing[1.5]};
  }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[0.75]};

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const BlogCategoryTagLink = styled(Link)<{
  $textColor: string;
  $backgroundColor: string;
}>`
  ${tagStyles}
  background-color: ${({ theme, $backgroundColor }) =>
    theme.colors[$backgroundColor]};
  color: ${({ theme, $textColor }) => theme.colors[$textColor]};
  text-decoration: none;

  .BlogCategoryTag__arrow {
    display: inline-block;
    max-width: 0;
    overflow: hidden;
    transition:
      max-width var(--global-motion),
      padding-inline-end var(--global-motion);
  }

  &:hover,
  &:active {
    .BlogCategoryTag__arrow {
      max-width: 1em;
      padding-inline-end: ${({ theme }) => theme.spacing[0.25]};
    }
  }
`;

/**
 * Note: looks like a generic Tag, but given the movement and placement of the
 * arrow, it made more sense to create a separate styled component for it
 * rather than trying to refactor the existing Tag structure to accommodate
 * this use case.
 */
const BlogCategoryTag = ({ tag }: { tag: BlogCategoryKey }) => {
  const category = BLOG_CATEGORY_DATA[tag];

  return (
    <BlogCategoryTagLink
      href={categoryUrl(tag)}
      $textColor={category.textColor}
      $backgroundColor={category.backgroundColor}
      onClick={() => trackLink('tag-blog', tag)}
    >
      <span className="BlogCategoryTag__arrow" aria-hidden="true">
        ←
      </span>
      {category.name}
    </BlogCategoryTagLink>
  );
};

/**
 * Blog breadcrumb bar for article pages.
 *
 * ⚠️ Intended for use in the **page templates**, not inside markdown content.
 */
const Breadcrumbs = ({ tags }: BreadcrumbsProps) => {
  return (
    <BreadcrumbsBar aria-label="blog article breadcrumbs">
      <BackToBlogLink />
      {tags && (
        <TagsContainer>
          {tags.map((tag) => (
            <BlogCategoryTag key={tag} tag={tag} />
          ))}
        </TagsContainer>
      )}
    </BreadcrumbsBar>
  );
};

export default Breadcrumbs;
